import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import { useAuthStore } from '@/stores/auth';
import { clickFirstRowIn, delay, tableSettledEmpty } from '@/utils/tour/actions';
import { logger } from '@/utils/logger';
import { waitForCondition, waitForTourElement } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for Contract Management. The spine follows the data
 * model: the vault holds contracts, contracts hold files, and the entities panel
 * ties files to the real world (parties, clauses, obligations). Every scripted
 * click is a navigation into the org's own records; nothing here writes to
 * the backend, so there is no residue to clean up.
 */

// Whether each "open the first …" hop actually clicked a record — set
// synchronously by the open actions, so the scenes on the detail pages can
// skip instantly on orgs without data instead of burning their target
// timeouts one by one. The route checks cover resumes landing mid-tour.
let contractOpened = false;
let fileOpened = false;

/**
 * Open the first contract in the vault listing. Only a contract row carries
 * the files-count badge, and the vault opens a row on a DOUBLE click (single
 * clicks select). A vault root without contracts skips the hop quietly.
 */
async function openFirstContract(ctx: TourActionContext): Promise<void> {
  contractOpened = false;
  const list = await waitForTourElement('vault.list', 3000);
  if (!list || ctx.isCancelled()) return;
  const badge = await waitForCondition(
    () =>
      list.querySelector<HTMLElement>('.badge.files') ?? (tableSettledEmpty(list) ? 'empty' : null),
    3000
  );
  if (!badge || badge === 'empty' || ctx.isCancelled()) {
    logger.warn('[tour] no contracts in the vault root — skipping the contract hop');
    return;
  }
  const row = badge.closest<HTMLElement>('.row');
  if (!row) return;
  row.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(row);
  if (ctx.animate) {
    await delay(700);
  }
  row.dispatchEvent(new MouseEvent('dblclick', { bubbles: true, cancelable: true, view: window }));
  contractOpened = !ctx.isCancelled();
}

async function openFirstFile(ctx: TourActionContext): Promise<void> {
  fileOpened = false;
  fileOpened = await clickFirstRowIn(ctx, 'cm.contract-files');
}

const contractOpen = (): boolean =>
  contractOpened || getRouter().currentRoute.value.name === 'contract-management-contract-detail';

const fileOpen = (): boolean =>
  fileOpened || getRouter().currentRoute.value.name === 'contract-management-contract-file-detail';

/**
 * Scenes on parameterized routes (contract detail, file detail) can't be
 * reached by an engine route push — going Back to them retraces browser
 * history instead, which is exactly the page the tour came from.
 */
async function goBackInHistory(): Promise<void> {
  getRouter().back();
  // Give the navigation a beat to commit before the previous scene resolves.
  await delay(500);
}

export const contractManagementTour: TourDefinition = {
  key: 'contract-management',
  title: 'Contract Management',
  description: 'From the vault to contracts, files, and the entities behind them.',
  entryRoute: 'file-vault',
  // Land back where the story started; nothing to clean (the demo only
  // navigated, it never wrote).
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'file-vault' });
    } catch {
      // Navigation declined; completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'vault-contracts',
      route: 'file-vault',
      target: 'vault.list',
      placement: 'top',
      title: 'Contracts in the vault',
      body:
        'Contracts live in the Contract Vault, organized in folders like any ' +
        'other document. Let’s open the first contract.',
      actions: [{ kind: 'custom', run: openFirstContract }],
    },
    {
      id: 'contract-info',
      prerequisite: contractOpen,
      target: 'cm.contract-info',
      placement: 'bottom',
      title: 'The contract overview',
      body: 'Name, type, owner, tags, and related matters. This page is the ' + 'contract’s home.',
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'contract-panel-tabs',
      prerequisite: contractOpen,
      target: 'cm.contract-panel-tabs',
      placement: 'bottom-end',
      title: 'Matters and sharing',
      body: 'Related matters and who this contract is shared with, side by side.',
    },
    {
      id: 'contract-files',
      prerequisite: contractOpen,
      target: 'cm.contract-files',
      placement: 'top',
      title: 'Files and versions',
      body: 'A contract can consist of several contract files. Let’s open one.',
      actions: [{ kind: 'custom', run: openFirstFile }],
    },
    {
      id: 'file-actions',
      prerequisite: fileOpen,
      target: 'cm.file-actions',
      placement: 'bottom-end',
      title: 'Everything you can do with this file',
      body: () =>
        useAuthStore().userPermissions.can_use_signing
          ? 'Download it, add a new revision, or send it for signature. ' +
            'Every version stays tracked.'
          : 'Download it or add a new revision. Every version stays tracked.',
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'file-extractions',
      prerequisite: fileOpen,
      target: 'cm.file-extractions',
      placement: 'top',
      title: 'What the AI read',
      body:
        'Extractions pull key values straight from the document: dates, ' +
        'parties, amounts. You can review and edit them here.',
    },
    {
      id: 'file-entities',
      prerequisite: fileOpen,
      target: 'cm.file-entities',
      placement: 'top',
      title: 'The entities in this file',
      body:
        'The parties to the document, and the clauses and obligations it ' +
        'contains, are linked here as entities.',
    },
    {
      id: 'finale',
      title: 'That is Contract Management',
      body:
        'The vault holds contracts, contracts hold files, and entities connect ' +
        'them to the real world. Explore the rest at your own pace.',
    },
  ],
};
